import ConsultationOffer from './pages/ConsultationOffer';
import DesignRecoveryOffer from './pages/DesignRecoveryOffer';
import { lazy, Suspense, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Realtimax tenant is locked to a single owner. Override via VITE_REALTIMAX_OWNER_EMAIL
// if the email changes; default keeps the lockdown working out of the box.
const REALTIMAX_OWNER_EMAIL =
  (import.meta.env.VITE_REALTIMAX_OWNER_EMAIL as string | undefined)?.toLowerCase() ??
  'jason@blissbunkbeds.com';

function isRealtimaxHost() {
  if (typeof window === 'undefined') return false;
  return window.location.hostname.includes('realtimax');
}
import { TenantProvider, useTenant } from './contexts/TenantContext';
import {
  applyThemeToDocument,
  applyRegionColorsToDocument,
  isBackOfficeSurface,
  type RegionColors,
} from './config/themes';
import { resolveThemeForTenant } from './config/hostTenants';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { restoreHeithInventory } from './utils/restoreHeithInventory';
// Not lazy: this route only redirects, so waiting on a chunk would stall the hop.
import ProposalShortLink from './pages/ProposalShortLink';
import { startDripWorker } from './services/dripWorker';
import { logger } from './utils/logger';
import { isBlissBasicHost } from './config/blissBasicHost';
import { isBlissDesignHost } from './config/blissDesignHost';
import BlissBasicSite from './pages/BlissBasicSite';
import BlissDesignConfigurator from './pages/BlissDesignConfigurator';
const BlissDesignerPortal = lazy(() => import('./pages/BlissDesignerPortal'));
import BlissBasicBackOffice from './pages/BlissBasicBackOffice';
import BlissBasicConfigurator from './components/bliss/BlissBasicConfigurator';
import BlissBasicPackageOfferings from './pages/BlissBasicPackageOfferings';

const BasicTeamPortal = lazy(() => import('./pages/BasicTeamPortal'));
const BasicBunkPreview = lazy(() => import('./pages/BasicBunkPreview'));
const PhotoBunkPreview = lazy(() => import('./components/bliss/photo/PhotoBunkPreview'));

// Lazy load all portal pages for better performance
const PublicQuoteBuilder = lazy(() => import('./components/PublicQuoteBuilder'));
const Operations = lazy(() => import('./pages/Operations'));
const AdminRoute = lazy(() => import('./shells/AdminRoute'));
const SalesPortal = lazy(() => import('./pages/SalesEntry'));
const CutlistPortal = lazy(() => import('./pages/CutlistPortal'));
const PartnerMobile = lazy(() => import('./pages/PartnerMobile'));
const Track = lazy(() => import('./pages/Track'));
const TrackOrder = lazy(() => import('./pages/TrackOrder'));
const ClientApproval = lazy(() => import('./pages/ClientApproval'));
const QuoteApproval = lazy(() => import('./pages/QuoteApproval'));
const InvoiceApproval = lazy(() => import('./pages/InvoiceApproval'));
const FinalInvoiceApproval = lazy(() => import('./pages/FinalInvoiceApproval'));
const Login = lazy(() => import('./pages/Login'));
const VoiceTest = lazy(() => import('./pages/VoiceTest'));
const PartnerApply = lazy(() => import('./pages/PartnerApply'));
const SMSOptIn = lazy(() => import('./pages/SMSOptIn'));
const InstallerPortal = lazy(() => import('./pages/InstallerPortal'));
const InstallerApply = lazy(() => import('./pages/InstallerApply'));
const DesignCheckoutSuccess = lazy(() => import('./pages/DesignCheckoutSuccess'));
const BlissSystemPreviewGate = lazy(() => import('./components/bliss/BlissSystemPreviewGate'));
const QuoteWizard = lazy(() => import('./pages/QuoteWizard'));
const ProposalFinder = lazy(() => import('./pages/ProposalFinder'));
const ManualViewer = lazy(() => import('./pages/ManualViewer'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const RealtiMaxLanding = lazy(() => import('./pages/RealtiMaxLanding'));
const RealtiMaxSignUp = lazy(() => import('./pages/RealtiMaxSignUp'));
const RealtiMaxVerify = lazy(() => import('./pages/RealtiMaxVerify'));
const RealtimaxManifesto = lazy(() => import('./pages/RealtimaxManifesto'));
const CommunityHub          = lazy(() => import('./pages/realtimax/CommunityHub'));
const ProfessionalProfile   = lazy(() => import('./pages/realtimax/ProfessionalProfile'));
const ProfileSetup          = lazy(() => import('./pages/realtimax/ProfileSetup'));
const PropertyPage          = lazy(() => import('./pages/realtimax/PropertyPage'));
const MasteryPage           = lazy(() => import('./pages/MasteryPage'));
const PlatformAdminView = lazy(() => import('./pages/PlatformAdminView'));
const SkinPreview        = lazy(() => import('./pages/SkinPreview'));
const PluginStore        = lazy(() => import('./pages/PluginStore'));
const TourEditor         = lazy(() => import('./pages/TourEditor'));
const GomaxReview        = lazy(() => import('./pages/gomax/Review'));
const EngagementProposalsList    = lazy(() => import('./pages/EngagementProposalsList'));
const EngagementProposalEditor   = lazy(() => import('./pages/EngagementProposalEditor'));
const EngagementProposalApproval = lazy(() => import('./pages/EngagementProposalApproval'));

// Loading fallback component
function PortalLoader() {
  const isBlissHost =
    typeof window !== 'undefined' &&
    (window.location.hostname.toLowerCase().includes('blissbunkbeds') ||
      window.location.hostname.toLowerCase().includes('bliss'));
  const isQuoteApprove =
    typeof window !== 'undefined' &&
    window.location.pathname.startsWith('/quote-approve/');

  const isBlissBasic =
    typeof window !== 'undefined' && isBlissBasicHost(window.location.hostname)
  const isBlissDesign =
    typeof window !== 'undefined' && isBlissDesignHost(window.location.hostname)
  const isBlissQuoteHost =
    isBlissHost &&
    typeof window !== 'undefined' &&
    (isBlissBasic ||
      isBlissDesign ||
      window.location.hostname.toLowerCase().includes('quote.') ||
      window.location.pathname.startsWith('/quote'))

  const loaderBg =
    isQuoteApprove && isBlissHost
      ? '#f4efe6'
      : isBlissQuoteHost
        ? '#f4efe6'
        : isBlissHost
          ? '#faf8f1'
          : '#1E2532'
  const loaderText = isBlissHost ? '#303729' : '#c9a962'

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: loaderBg,
      color: loaderText,
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid rgba(201, 169, 98, 0.25)',
          borderTopColor: '#c9a962',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 16px'
        }} />
        <p>Loading portal...</p>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// =============================================================================
// REALTIMAX ACCESS GATE
// On any *.realtimax.* host, allow access only to the owner email. Everyone
// else is bounced to /login. Any non-owner who somehow holds a session is
// signed out automatically. Path /login stays reachable so the owner has a
// way in. Other hosts are unaffected.
// =============================================================================
// Public marketing surface for realtimax.com. Anything not on this list is
// owner-only (admin, sales, operations, customer data, etc.).
const REALTIMAX_PUBLIC_PATHS = new Set<string>([
  '/', '/login',
  '/signup', '/verify', '/about', '/manifesto', '/network', '/mastery',
]);
const REALTIMAX_PUBLIC_PREFIXES = ['/realtimax', '/property/'];

function isRealtimaxPublicPath(pathname: string): boolean {
  if (REALTIMAX_PUBLIC_PATHS.has(pathname)) return true;
  return REALTIMAX_PUBLIC_PREFIXES.some(p => pathname.startsWith(p));
}

function RealtimaxAccessGate({ children }: { children: React.ReactNode }) {
  const { user, loading, signOut } = useAuth();
  const location = useLocation();
  const isRealtimax = isRealtimaxHost();
  const isOwner = !!user && user.email?.toLowerCase() === REALTIMAX_OWNER_EMAIL;

  useEffect(() => {
    if (!isRealtimax || loading) return;
    if (user && !isOwner) {
      // Authenticated as someone else → sign out so the session can't sneak
      // back through any per-route auth check downstream.
      signOut();
    }
  }, [isRealtimax, loading, user, isOwner, signOut]);

  if (!isRealtimax) return <>{children}</>;
  if (loading) return null;
  if (isOwner) return <>{children}</>;

  // Non-owners get the public marketing surface (Max Front Door, sign up,
  // about, community, public property pages). Anything else bounces back to
  // the front door — not to /login, because the door IS the welcome.
  if (isRealtimaxPublicPath(location.pathname)) return <>{children}</>;
  return <Navigate to="/" replace />;
}

/**
 * Re-applies the theme whenever the route changes.
 *
 * applyThemeToDocument re-surfaces back-office routes onto pearl, but the
 * tenant loader only runs it once. Without this, hopping from a client page
 * into /sales (or back out) would keep whichever stage was painted first.
 */
function SurfaceThemeSync() {
  const location = useLocation();
  const { tenantSlug, themePreset, primaryColor, settings, isLoading } = useTenant();
  const regionColors = settings?.regionColors as RegionColors | undefined;

  useEffect(() => {
    if (isLoading) return;
    if (location.pathname === '/login') return;
    applyThemeToDocument(resolveThemeForTenant(tenantSlug, themePreset, primaryColor));
    // Per-region background overrides are a tenant's own branding for client
    // surfaces; the back office is deliberately uniform, so they are dropped.
    applyRegionColorsToDocument(isBackOfficeSurface(location.pathname) ? null : regionColors);
  }, [location.pathname, tenantSlug, themePreset, primaryColor, regionColors, isLoading]);

  return null;
}

// Domain-based home page routing
// ops.blissbunkbeds.com -> redirects to /operations
// millworks.blissbunkbeds.com -> redirects to /cutlist
// quote.blissbunkbeds.com (and others) -> Quote Builder
function HomeRouter() {
  const hostname = window.location.hostname;
  const { loading } = useAuth();

  // Domain-based routing
  if (hostname.includes('admin')) {
    return <Navigate to="/admin" replace />;
  }

  if (hostname.includes('sales')) {
    return <Navigate to="/sales" replace />;
  }

  if (hostname.includes('ops')) {
    return <Navigate to="/operations" replace />;
  }

  if (hostname.includes('millworks')) {
    return <Navigate to="/millworks" replace />;
  }

  if (hostname.includes('installer')) {
    return <Navigate to="/installer" replace />;
  }

  if (isBlissDesignHost(hostname)) {
    return <BlissDesignConfigurator />;
  }

  if (isBlissBasicHost(hostname)) {
    return <BlissBasicSite />;
  }

  // Realtimax has a public marketing surface (Max Front Door + sign up +
  // community) AND a locked-down owner-only admin tenant. The gate
  // (RealtimaxAccessGate, wrapping <Routes>) enforces who can reach what.
  // Even the owner lands at the front door first — a "Sign In" link in the
  // landing's top-right is how they explicitly choose to enter /admin.
  if (hostname.includes('realtimax')) {
    if (loading) return null;
    return <RealtiMaxLanding />;
  }

  // Default: show Public Quote Builder (with Lead Gate)
  return <PublicQuoteBuilder />;
}

const log = logger.module('App');

function App() {
  const inventoryInitialized = useRef(false);
  const path = window.location.pathname;
  
  // Fast-path: client-facing approval pages skip AuthProvider + background workers entirely
  const isClientApprovalPage = path === '/consultation-offer' || path === '/design-offer' || path === '/basic-team' || path === '/basic-preview/twin-xl-over-queen' || path.startsWith('/quote-approve/') || 
                                path.startsWith('/p/') ||
                                path.startsWith('/approve/') || 
                                path.startsWith('/invoice-sign/') ||
                                path.startsWith('/final-invoice/') ||
                                path.startsWith('/engage/');

  // Hooks MUST be called unconditionally (React rules of hooks)
  useEffect(() => {
    if (isClientApprovalPage) return; // Skip for approval pages
    if (import.meta.env.DEV && !inventoryInitialized.current) {
      log.debug('DEV: Initializing Heith test inventory...');
      restoreHeithInventory();
      inventoryInitialized.current = true;
    }
  }, [isClientApprovalPage]);
  
  useEffect(() => {
    if (isClientApprovalPage) return; // Skip for approval pages
    const timer = setTimeout(() => {
      log.debug('Starting drip campaign worker...');
      startDripWorker();
    }, 10000);
    
    return () => clearTimeout(timer);
  }, [isClientApprovalPage]);

  if (isClientApprovalPage) {
    return (
      <BrowserRouter>
        <Suspense fallback={<PortalLoader />}>
          <Routes>
            <Route path="/basic-team" element={<BasicTeamPortal />} />
            <Route path="/basic-preview/twin-xl-over-queen" element={<BasicBunkPreview />} />
            <Route path="/consultation-offer" element={<ConsultationOffer />} />
            <Route path="/design-offer" element={<DesignRecoveryOffer />} />
            <Route path="/quote-approve/:token" element={<QuoteApproval />} />
            <Route path="/p/:code" element={<ProposalShortLink />} />
            <Route path="/approve/:token" element={<ClientApproval />} />
            <Route path="/invoice-sign/:orderId" element={<InvoiceApproval />} />
            <Route path="/final-invoice/:orderId" element={<FinalInvoiceApproval />} />
            <Route path="/engage/:token" element={<EngagementProposalApproval />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    );
  }
  
  return (
    <TenantProvider>
    <AuthProvider>
      <BrowserRouter>
        <SurfaceThemeSync />
        <Suspense fallback={<PortalLoader />}>
          <RealtimaxAccessGate>
          <Routes>
          {/* PUBLIC ROUTES */}
          <Route path="/" element={<HomeRouter />} />
          <Route path="/quote" element={<PublicQuoteBuilder />} />
          <Route path="/basic" element={<BlissBasicSite />} />
          <Route path="/options/packages" element={<BlissBasicPackageOfferings />} />
          <Route path="/options" element={<BlissBasicConfigurator />} />
          <Route path="/design" element={<BlissDesignConfigurator />} />
          <Route path="/designer" element={<BlissDesignerPortal />} />
          <Route path="/design/photo-prototype" element={<PhotoBunkPreview />} />
          <Route path="/track" element={<Track />} />
          <Route path="/track/:orderId" element={<TrackOrder />} />
          <Route path="/approve/:token" element={<ClientApproval />} />
          <Route path="/consultation-offer" element={<ConsultationOffer />} />
            <Route path="/design-offer" element={<DesignRecoveryOffer />} />
            <Route path="/quote-approve/:token" element={<QuoteApproval />} />
          <Route path="/p/:code" element={<ProposalShortLink />} />
          <Route path="/engage/:token" element={<EngagementProposalApproval />} />
          <Route path="/invoice-sign/:orderId" element={<InvoiceApproval />} />
          <Route path="/final-invoice/:orderId" element={<FinalInvoiceApproval />} />
          <Route path="/login" element={<Login />} />
          <Route path="/realtimax" element={<RealtiMaxLanding />} />
          <Route path="/realtimax/signup" element={<RealtiMaxSignUp />} />
          <Route path="/realtimax/verify" element={<RealtiMaxVerify />} />
          <Route path="/realtimax/manifesto" element={<RealtimaxManifesto />} />
          <Route path="/realtimax/about" element={<RealtimaxManifesto />} />
          <Route path="/realtimax/network" element={<CommunityHub />} />
          <Route path="/network" element={<CommunityHub />} />
          <Route path="/realtimax/professional/:profileId" element={<ProfessionalProfile />} />
          <Route path="/realtimax/profile/setup" element={<ProfileSetup />} />
          <Route path="/realtimax/property/:propertyId" element={<PropertyPage />} />
          <Route path="/property/:propertyId" element={<PropertyPage />} />
          <Route path="/mastery" element={<MasteryPage />} />
          <Route path="/signup" element={<RealtiMaxSignUp />} />
          <Route path="/verify" element={<RealtiMaxVerify />} />
          <Route path="/manifesto" element={<RealtimaxManifesto />} />
          <Route path="/about" element={<RealtimaxManifesto />} />
          <Route path="/onboarding" element={isBlissBasicHost(window.location.hostname)
            ? <Navigate to="/basic-admin" replace />
            : <Onboarding />} />
          <Route path="/voice-test" element={<VoiceTest />} />
          <Route path="/apply" element={<PartnerApply />} />
          <Route path="/sms-optin" element={<SMSOptIn />} />
          <Route path="/design/success" element={<DesignCheckoutSuccess />} />
          <Route path="/bliss/system-preview" element={<BlissSystemPreviewGate />} />
          <Route path="/q/:code" element={<QuoteWizard />} />
          <Route path="/q" element={<QuoteWizard />} />
          <Route path="/manuals/:slug" element={<ManualViewer />} />
          
          {/* PROTECTED ROUTES - Require authentication */}
          <Route path="/sales" element={
            <ProtectedRoute>
              <SalesPortal />
            </ProtectedRoute>
          } />
          <Route path="/find-proposal" element={
            <ProtectedRoute>
              <ProposalFinder />
            </ProtectedRoute>
          } />
          <Route path="/operations" element={
            <ProtectedRoute>
              {isBlissBasicHost(window.location.hostname)
                ? <Navigate to="/basic-admin" replace />
                : <Operations />}
            </ProtectedRoute>
          } />
          <Route path="/millworks" element={
            <ProtectedRoute>
              <CutlistPortal />
            </ProtectedRoute>
          } />
          <Route path="/cutlist" element={<Navigate to="/millworks" replace />} />
          <Route path="/app" element={<PartnerMobile />} />
          <Route path="/installer" element={<InstallerPortal />} />
          <Route path="/installer-apply" element={<InstallerApply />} />
          <Route path="/install-partner" element={<InstallerApply initialType="installer" hideToggle />} />
          <Route path="/millworks-partner" element={<InstallerApply initialType="fabricator" hideToggle />} />
          <Route path="/basic-admin" element={
            <ProtectedRoute requiredRole="admin">
              <BlissBasicBackOffice />
            </ProtectedRoute>
          } />
          <Route path="/admin" element={
            <ProtectedRoute requiredRole="admin">
              <AdminRoute />
            </ProtectedRoute>
          } />
          {/* Realtimax super-admin fleet view. Component itself redirects
              users without is_platform_admin = TRUE back to /admin, so the
              ProtectedRoute only guards authentication. */}
          <Route path="/platform" element={
            <ProtectedRoute>
              <PlatformAdminView />
            </ProtectedRoute>
          } />
          <Route path="/platform/skins" element={
            <ProtectedRoute>
              <SkinPreview />
            </ProtectedRoute>
          } />
          <Route path="/plugins" element={
            <ProtectedRoute>
              <PluginStore />
            </ProtectedRoute>
          } />
          {/* Engagement Overview proposals — the section-based, signable
              contract builder. List + editor are admin-only; the public
              recipient view at /engage/:token is wired above (and in the
              fast-path) so it works for unauthenticated visitors. */}
          <Route path="/admin/proposals" element={
            <ProtectedRoute requiredRole="admin">
              <EngagementProposalsList />
            </ProtectedRoute>
          } />
          <Route path="/admin/proposals/:id" element={
            <ProtectedRoute requiredRole="admin">
              <EngagementProposalEditor />
            </ProtectedRoute>
          } />
          <Route path="/platform/tour-editor" element={
            <ProtectedRoute>
              <TourEditor />
            </ProtectedRoute>
          } />
          {/* GoMaxAI review queue — human gate for every diagnosed pain point
              and every drafted tool spec. Gated by is_platform_admin (the
              component itself redirects non-admins to /admin). */}
          <Route path="/platform/gomax/review" element={
            <ProtectedRoute>
              <GomaxReview />
            </ProtectedRoute>
          } />
          </Routes>
          </RealtimaxAccessGate>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
    </TenantProvider>
  );
}

export default App;
